import axios from 'axios';
import { axiosRequest, DHLError, handleHttpResponse, getCurrentDate, SubmitPolicyError, SubmitPolicyInProcessError, SubmitPolicyTimeoutError } from '@general/js/utils';
import { PolicyErrorCodesEnum } from '@general/js/enums';
import Services from '@/services/Services';
import GeneralServices from '@general/js/services/Services';
import { EnvironmentVariables } from '@general/js/environmentVariables';
import ChangeProductAbstractServices from './ChangeProductAbstractServices';

export default class ChangeProductServices extends ChangeProductAbstractServices {
  /**
   * Implicitly returns a Promise for content information on change product, capture additional information and success confirmation.
   *
   * @returns {object} - object of change product page reference data
   */
  static async getChangeProductReferenceData() {
    try {
      const response = await axios.get(Services.MY_COVER.CHANGE_COVER.CHANGE_PRODUCT.GET_REFERENCE_DATA);

      if (handleHttpResponse(response)) {
        return JSON.parse(JSON.stringify(response.data).tokenSwap());
      }

      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`);
    } catch (error) {
      throw new DHLError(`getChangeProductReferenceData() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Get the membership details along with members, products & eligibility details
   * @param {string} memberNumber Member number
   * @returns {Object} with different types of membership details
   */
  static async getMembershipDetails(memberNumber) {
    try {
      const response = await axiosRequest().get(Services.MY_COVER.CHANGE_COVER.RETRIEVE_COVER_DETAILS(memberNumber, getCurrentDate()));

      return handleHttpResponse(response);
    } catch (error) {
      throw new DHLError(`getMembershipDetails() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Get the recommended products after needs analysis is completed
   * @param {Object} payload Payload object
   */
  static async getRecommendedProducts(payload) {
    try {
      const response = await axiosRequest().post(Services.MY_COVER.CHANGE_COVER.GET_PRODUCT_RECOMMENDATION, payload);

      return handleHttpResponse(response);
    } catch (error) {
      throw new DHLError(`getRecommendedProducts() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Get termination reasons
   * @returns {Object} with different types of termination reasons
   */
  static async getTerminationReasonFlexCodes() {
    try {
      const response = await axiosRequest().get(Services.MY_COVER.CHANGE_COVER.GET_TERMINATION_REASONS(getCurrentDate()));

      if (handleHttpResponse(response)) {
        return response.data.terminationReasons;
      }

      throw new DHLError(`Issue with the getTerminationReasonFlexCodes() response: ${response}`);
    } catch (error) {
      throw new DHLError(`getTerminationReasonFlexCodes() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Get Competitor Fund Names
   * @returns {Array} of Fund Names
   */
  static async getFundNames() {
    try {
      const response = await axiosRequest().get(Services.MY_COVER.CHANGE_COVER.GET_FUND_NAMES);

      if (handleHttpResponse(response)) {
        return response.data.fundNames;
      }

      throw new DHLError(`Issue with the response: ${response}`);
    } catch (error) {
      throw new DHLError(`getFundNames() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Function to check if the member is duplicate based on the search criteria of 4 fields i.e first name, last name, gender, dob
   * @param {string} firstName First name to search
   * @param {string} lastName Last name to search
   * @param {string} gender Gender to search in format `M/F`
   * @param {string} dob Date of birth to search in format `yyyy-mm-dd`
   * @returns True/False if the member is duplicate based on the search criteria
   */
  static async isPersonDuplicate(firstName, lastName, gender, dob) {
    try {
      const response = await axiosRequest().post(Services.MY_COVER.CHANGE_COVER.GET_DUPLICATE_MEMBERS, { firstName, lastName, gender, dateOfBirth: dob });

      if (handleHttpResponse(response)) {
        return response.data.isMemberDuplicate;
      }

      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`);
    } catch (error) {
      throw new DHLError(`isPersonDuplicate() - call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Function to raise a work item in CRM when member tried to add a duplicate person
   * @param {string} memberNumber Member number (policy number)
   * @param {Object} payload Payload to be sent for the work item, containing details like first name, last name, gender, dob of duplicate person along with needs analysis details
   * @returns
   */
  static async raiseDuplicatePersonWorkItem(memberNumber, payload) {
    try {
      const response = await axiosRequest().post(Services.COMMON.POST_COMMON_WORK_ITEM(memberNumber), payload);

      return handleHttpResponse(response);
    } catch (error) {
      throw new DHLError(`raiseDuplicatePersonWorkItem() - call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Function to get the over coverage details for a member based on their personCode
   * @param {string} personCode personCode of the member to check over coverage for
   * @returns {Object} Object with `duplicateMembers` array which has all products this `personCode` is currently covered on
   */
  static async getPersonOverCoverage(personCode) {
    try {
      const response = await axiosRequest().get(Services.MY_COVER.CHANGE_COVER.GET_PERSON_OVER_COVERAGE(personCode, getCurrentDate()));

      return handleHttpResponse(response);
    } catch (error) {
      throw new DHLError(`getPersonOverCoverage() - call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Function to submit the change cover request for a member
   * @param {string} memberNumber Member number (policy number)
   * @param {Object} payload Submit change cover payload
   * @returns {Object} Submit change cover response
   */
  static async submitChangeCover(memberNumber, payload) {
    try {
      const response = await axiosRequest().post(Services.MY_COVER.CHANGE_COVER.SUBMIT_CHANGE_COVER(memberNumber), payload, {
        timeout: Number(EnvironmentVariables.MemberPortalTimeout.TIMEOUT),
      });

      return handleHttpResponse(response);
    } catch (error) {
      const policyErrorCode = error?.response?.data?.Error?.Code;
      if (policyErrorCode === PolicyErrorCodesEnum.SUBMIT_POLICY_ERROR) {
        throw new SubmitPolicyError(`submitChangeCover() - call failed with error: ${JSON.stringify(error.response.data.Error.ErrorMessage)}`, error.response.data.Error);
      } else if (error.code === PolicyErrorCodesEnum.TIMEOUT_ERROR) { // error.code is set by axios upon timeout
        throw new SubmitPolicyTimeoutError(`submitChangeCover() - call failed with error: ${JSON.stringify(error.message)}`, error);
      } else if (policyErrorCode === PolicyErrorCodesEnum.SUBMIT_POLICY_IN_PROCESS_ERROR) {
        throw new SubmitPolicyInProcessError(`submitChangeCover() - call failed with error: ${JSON.stringify(error.response.data.Error.ErrorMessage)}`, error.response.data.Error);
      }
      throw new DHLError(`submitChangeCover() - call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Function to fetch price for products either on a) My recommendations page after needs analysis (min payload) or b) Change cover - CAI Confirmation step (max payload)
   * @param {Object} payload Minimum or maximum payload based on where this service is being called from
   * @returns {Object} Object with `productPrices` array which has all product prices and discounts
   */
  static async getPrice(payload) {
    try {
      const response = await axiosRequest().post(GeneralServices.JOIN_NOW.GET_PRODUCT_PRICE, payload);

      if (handleHttpResponse(response) && Array.isArray(response.data.productPrices) && response.data.productPrices.length > 0) {
        return {
          productPrices: response.data.productPrices.map((productPrice) => {
            return { ...productPrice, productCodes: productPrice.enrollmentProductCode };
          }),
        };
      }

      throw new DHLError(`Issue with the getPrice() response: ${response}`);
    } catch (error) {
      throw new DHLError(`getPrice() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Function to send email quote to the member
   * @param {Object} payload Email quote payload
   * @param {string} memberNumber Member number (policy number)
   */
  static async postEmailQuote(payload, memberNumber) {
    try {
      const response = await axiosRequest().post(Services.MY_COVER.CHANGE_COVER.POST_EMAIL_QUOTE(memberNumber), payload);

      return handleHttpResponse(response);
    } catch (error) {
      throw new DHLError(`postEmailQuote() - call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Fetches all the policies on the accessing person. 
   * It is used to check if a person has multiple policies in CRM. 
   * The requesting person needs to be on the requesting policy.
   * @param {string} memberNumber membership id (policy number)
   * @param {string} accessingPersonCode
   * @returns {object}
   */
  static async getMemberPolicies(memberNumber, accessingPersonCode) {
    try {
      const response = await axiosRequest().get(Services.COMMON.GET_MEMBER_POLICIES(memberNumber, accessingPersonCode, getCurrentDate()));

      if (handleHttpResponse(response)) {
        const data = { ...response.data };

        return data;
      }

      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`);
    } catch (error) {
      throw new DHLError(`getMemberPolicies() - call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }
}
